import React, { useRef, useEffect } from 'react'
import { useAudio } from '../hooks/useAudio'
import { useOrientation } from '../hooks/useOrientation'
import { useSoundManager } from '../hooks/useSoundManager'
import SoundItem from './SoundItem'
import RecordingPanel from './RecordingPanel'
import FileUploadPanel from './FileUploadPanel'
import { getCategoryStats } from '../utils/soundUtils'
import { fileUploadService } from '../services/fileUploadService'

const SoundList: React.FC = () => {
  const { playSound, stopSound, getAudioState } = useAudio()
  const { isPortrait, isLandscape } = useOrientation()
  const { sounds, addSound, removeSound, loading } = useSoundManager()
  const listRef = useRef<HTMLDivElement>(null)

  const handlePlay = (soundPath: string, soundData: any) => {
    playSound(soundPath, soundData)
  }

  const handleStop = (soundPath: string) => {
    stopSound(soundPath)
  }

  const handleRecordingComplete = async (blob: Blob, label: string) => {
    try {
      await addSound(blob, label, 'custom')
    } catch (error) {
      console.error('Failed to save recording:', error)
    }
  }

  const handleFilesUploaded = async (files: { file: File; label: string; category: string }[]) => {
    try {
      for (const fileData of files) {
        const blob = await fileUploadService.fileToBlob(fileData.file)
        await addSound(blob, fileData.label, fileData.category)
      }
    } catch (error) {
      console.error('Failed to save uploaded files:', error)
    }
  }

  const handleDeleteSound = (soundId: string) => {
    if (window.confirm('Are you sure you want to delete this sound? This action cannot be undone.')) {
      removeSound(soundId)
    }
  }

  // Sort sounds by creation date
  const sortedSounds = [...sounds].sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  // Smooth scrolling effect
  useEffect(() => {
    if (listRef.current) {
      listRef.current.style.scrollBehavior = 'smooth'
    }
  }, [])

  // Handle orientation changes
  useEffect(() => {
    if (listRef.current) {
      if (isLandscape) {
        listRef.current.style.maxHeight = '60vh'
      } else {
        listRef.current.style.maxHeight = 'none'
      }
    }
  }, [isPortrait, isLandscape])

  const categoryStats = getCategoryStats(sounds)

  if (loading) {
    return (
      <div className="sound-list">
        <div className="sound-list-loading">
          <p>Loading sounds...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sound-list">
      {/* Recording Panel */}
      <RecordingPanel onRecordingComplete={handleRecordingComplete} />

      {/* File Upload Panel */}
      <FileUploadPanel onFilesUploaded={handleFilesUploaded} />

      <div className="sound-list-header">
        <p className="sound-list-count">{sortedSounds.length} sounds available</p>

        {/* Category overview */}
        {categoryStats.length > 0 && (
          <div className="category-stats">
            {categoryStats.map((category) => (
              <div key={category.name} className="category-stat">
                <span className="category-icon">{category.icon}</span>
                <span className="category-name">{category.displayName}</span>
                <span className="category-count">({category.count})</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="sound-list-items" ref={listRef}>
        {sortedSounds.map((sound) => (
          <SoundItem
            key={sound.id}
            sound={sound}
            audioState={getAudioState(sound.filePath)}
            onPlay={(soundPath) => handlePlay(soundPath, sound)}
            onStop={handleStop}
            onDelete={handleDeleteSound}
          />
        ))}
      </div>

      {sortedSounds.length === 0 && (
        <div className="sound-list-empty">
          <p>No sounds recorded yet. Use the recording panel above to add your first sound!</p>
        </div>
      )}
    </div>
  )
}

export default SoundList